import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import { parseDocs } from './parseDocs';
import {
  evenementInputSchema,
  evenementSchema,
  type Evenement,
  type EvenementInput,
} from '../schemas/evenement';

const COLLECTION = 'evenements';

function db() {
  return getFirebaseClients().firestore;
}

function parse(snapshotData: unknown, id: string): Evenement {
  const data = snapshotData as Record<string, unknown>;
  return evenementSchema.parse({
    ...data,
    id,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

/** Public reads — published events ordered by startDate ascending. */
export async function listPublishedEvenements(): Promise<Evenement[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('status', '==', 'published'),
    orderBy('startDate', 'asc'),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

/** Admin reads — all events, ordered by startDate descending (newest first). */
export async function listAllEvenements(): Promise<Evenement[]> {
  const q = query(collection(db(), COLLECTION), orderBy('startDate', 'desc'));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function getEvenement(id: string): Promise<Evenement | null> {
  const snap = await getDoc(doc(db(), COLLECTION, id));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

export async function createEvenement(input: EvenementInput): Promise<string> {
  const parsed = evenementInputSchema.parse(input);
  const now = new Date().toISOString();
  await setDoc(doc(db(), COLLECTION, parsed.slug), {
    ...parsed,
    createdAt: now,
    updatedAt: now,
  });
  return parsed.slug;
}

export async function updateEvenement(
  id: string,
  patch: Partial<EvenementInput>,
): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteEvenement(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
